import { POST_CHANNEL, isStoryPlatform } from '@constants/social-posts';
import { KB, MB, MEDIA_TYPE } from '@constants/file';
import { formatVideoDuration, formatVideoSize } from '@helpers/upload-file';
import {
    testEditorLength,
    testXWeightedLength,
    getEditorContentLength,
    getXWeightedLength,
} from '@helpers/text-editor';

// Ordered under the transport chain: this ≤ Caddy request_body (1005mb) ≤ [RequestSizeLimit].
export const VIDEO_UPLOAD_TRANSPORT_CAP = 1000 * MB;

const VIDEO_MIN_DURATION_SECONDS = 3;
const VIDEO_MIN_SIZE = 1 * KB;

// Widest ceiling any platform accepts (TikTok); per-platform specs narrow it.
const VIDEO_MAX_DIMENSION = 4096;

const VIDEO_PRODUCT_CAP = { maxBytes: 1000 * MB, maxDurationSeconds: 15 * 60 };

// Per-platform publishing limits surfaced in the UI. The `video` specs mirror SocialVideoLimits.cs
// and are advisory only — that file enforces them at save. `maxMessageLength` mirrors the backend:
// Google/Facebook/Instagram/TikTok/LinkedIn are pre-flighted by plain length and X by weighted
// length in CampaignExpansionService. Keep these in sync with the .NET constants.
export const PLATFORM_REQUIREMENTS = {
    [POST_CHANNEL.GOOGLE]: {
        maxMessageLength: 1500, // GoogleConstants.SocialPostMaxLength
        maxMedia: 1,
        extensions: '.png, .jpg, .jpeg',
    },
    [POST_CHANNEL.FACEBOOK]: {
        maxMessageLength: 63206, // FacebookConstants.FacebookPostMaxLength
        maxMedia: 10,
        extensions: '.png, .jpg, .jpeg, .mp4',
        video: { ...VIDEO_PRODUCT_CAP, maxWidth: 4096, maxHeight: 2304 }, // platform allows 10 GB / 240 min
    },
    [POST_CHANNEL.INSTAGRAM]: {
        maxMessageLength: 2200, // FacebookConstants.InstagramCaptionMaxLength
        maxMedia: 10,
        requiresMedia: true,
        extensions: '.png, .jpg, .jpeg, .mp4',
        video: { maxBytes: 300 * MB, maxDurationSeconds: 15 * 60, maxWidth: 1920, maxHeight: 1920 }, // Reels spec
        carouselVideo: { maxBytes: 100 * MB, maxDurationSeconds: 60, maxWidth: 1920, maxHeight: 1920 },
    },
    [POST_CHANNEL.TIKTOK]: {
        maxMessageLength: 2200, // TikTokConstants.CaptionMaxLength
        maxMedia: 35,
        extensions: '.png, .jpg, .jpeg, .mp4',
        video: { maxBytes: VIDEO_PRODUCT_CAP.maxBytes, maxDurationSeconds: 10 * 60, maxWidth: 4096, maxHeight: 4096 }, // platform allows 4 GB
    },
    [POST_CHANNEL.X]: {
        maxMessageLength: 280, // XConstants.StandardTextMaxLength; pre-flighted server-side by weighted length
        maxMedia: 4,
        extensions: '.png, .jpg, .jpeg, .mp4',
        weightedLength: true,
        video: { maxBytes: 512 * MB, maxDurationSeconds: 140, maxWidth: 1920, maxHeight: 1920 },
    },
    [POST_CHANNEL.LINKEDIN]: {
        maxMessageLength: 3000, // LinkedInConstants.PostMaxLength
        maxMedia: 20,
        extensions: '.png, .jpg, .jpeg, .mp4',
        // Videos API documented bounds: 75 KB – 500 MB, up to 30 min (product cap binds duration).
        video: { maxBytes: 500 * MB, maxDurationSeconds: 15 * 60, minBytes: 75 * KB, maxWidth: 4096, maxHeight: 2304 },
    },
    [POST_CHANNEL.FACEBOOK_STORY]: {
        maxMedia: 1,
        requiresMedia: true,
        extensions: '.png, .jpg, .jpeg, .mp4',
        video: { maxBytes: 100 * MB, maxDurationSeconds: 60, maxWidth: 1920, maxHeight: 1920 },
    },
    [POST_CHANNEL.INSTAGRAM_STORY]: {
        maxMedia: 1,
        requiresMedia: true,
        extensions: '.png, .jpg, .jpeg, .mp4',
        video: { maxBytes: 100 * MB, maxDurationSeconds: 60, maxWidth: 1920, maxHeight: 1920 },
    },
};

export const getVideoSpec = (platform, mediaCount = 1) => {
    const req = PLATFORM_REQUIREMENTS[platform];
    if (!req?.video) return null;
    if (mediaCount > 1 && req.carouselVideo) return req.carouselVideo;
    return req.video;
};

export const getVideoUploadValidation = (platforms = [], mediaCount = 1) => {
    const specs = platforms.map(p => getVideoSpec(p, mediaCount)).filter(Boolean);
    const maxBytes = specs.length ? Math.max(...specs.map(s => s.maxBytes)) : VIDEO_PRODUCT_CAP.maxBytes;
    const maxDurationSeconds = specs.length
        ? Math.max(...specs.map(s => s.maxDurationSeconds))
        : VIDEO_PRODUCT_CAP.maxDurationSeconds;
    const minBytes = specs.length ? Math.min(...specs.map(s => s.minBytes ?? VIDEO_MIN_SIZE)) : VIDEO_MIN_SIZE;
    const maxWidth = specs.length ? Math.max(...specs.map(s => s.maxWidth)) : VIDEO_MAX_DIMENSION;
    const maxHeight = specs.length ? Math.max(...specs.map(s => s.maxHeight)) : VIDEO_MAX_DIMENSION;
    return {
        maxSize: Math.min(VIDEO_UPLOAD_TRANSPORT_CAP, maxBytes),
        minSize: Math.max(VIDEO_MIN_SIZE, minBytes),
        dimension: [undefined, undefined, maxWidth, maxHeight],
        minDuration: VIDEO_MIN_DURATION_SECONDS,
        maxDuration: maxDurationSeconds,
    };
};

export const captionMediaCount = (items = []) =>
    items.some(m => m.type === MEDIA_TYPE.VIDEO) ? items.length : items.length + 1;

export const isVideoUnsupported = (platforms = []) =>
    platforms.length > 0 && platforms.every(platform => !getVideoSpec(platform));

export const getVideoConstraint = (platforms = [], mediaCount = 1) => {
    const entries = platforms
        .map(platform => ({ platform, spec: getVideoSpec(platform, mediaCount) }))
        .filter(e => e.spec);
    if (!entries.length) {
        return {
            maxBytes: Math.min(VIDEO_UPLOAD_TRANSPORT_CAP, VIDEO_PRODUCT_CAP.maxBytes),
            maxDurationSeconds: VIDEO_PRODUCT_CAP.maxDurationSeconds,
            minDurationSeconds: VIDEO_MIN_DURATION_SECONDS,
            maxWidth: VIDEO_MAX_DIMENSION,
            maxHeight: VIDEO_MAX_DIMENSION,
            sizeLimitedBy: null,
            durationLimitedBy: null,
        };
    }
    const bySize = entries.reduce((a, b) => (b.spec.maxBytes < a.spec.maxBytes ? b : a));
    const byDuration = entries.reduce((a, b) => (b.spec.maxDurationSeconds < a.spec.maxDurationSeconds ? b : a));
    const byResolution = entries.reduce((a, b) => {
        const longSide = spec => Math.max(spec.maxWidth, spec.maxHeight);
        const shortSide = spec => Math.min(spec.maxWidth, spec.maxHeight);
        return longSide(b.spec) < longSide(a.spec) ||
            (longSide(b.spec) === longSide(a.spec) && shortSide(b.spec) < shortSide(a.spec))
            ? b
            : a;
    });
    return {
        maxBytes: Math.min(VIDEO_UPLOAD_TRANSPORT_CAP, bySize.spec.maxBytes),
        maxDurationSeconds: byDuration.spec.maxDurationSeconds,
        minDurationSeconds: VIDEO_MIN_DURATION_SECONDS,
        maxWidth: byResolution.spec.maxWidth,
        maxHeight: byResolution.spec.maxHeight,
        sizeLimitedBy:
            bySize.spec.maxBytes < Math.min(VIDEO_UPLOAD_TRANSPORT_CAP, VIDEO_PRODUCT_CAP.maxBytes)
                ? bySize.platform
                : null,
        durationLimitedBy:
            byDuration.spec.maxDurationSeconds < VIDEO_PRODUCT_CAP.maxDurationSeconds ? byDuration.platform : null,
    };
};

// Saved videos carry no byte size (the server model drops it), so size rules skip them.
export const getVideoViolations = (platform, media = []) => {
    const spec = getVideoSpec(platform, media.length);
    if (!spec) return { tooLarge: false, tooLong: false, tooSmall: false, tooWide: false, spec: null };
    const videos = media.filter(m => m.type === MEDIA_TYPE.VIDEO);
    return {
        tooLarge: videos.some(v => v.size != null && v.size > spec.maxBytes),
        tooLong: videos.some(v => v.duration != null && v.duration > spec.maxDurationSeconds),
        tooSmall: !!spec.minBytes && videos.some(v => v.size != null && v.size < spec.minBytes),
        tooWide: videos.some(
            v =>
                v.width != null &&
                v.height != null &&
                (Math.max(v.width, v.height) > Math.max(spec.maxWidth, spec.maxHeight) ||
                    Math.min(v.width, v.height) > Math.min(spec.maxWidth, spec.maxHeight)),
        ),
        spec,
    };
};

const isPlatformWeighted = platform => !!PLATFORM_REQUIREMENTS[platform]?.weightedLength;

export const getPlatformLengthCounter = platform =>
    isPlatformWeighted(platform) ? getXWeightedLength : getEditorContentLength;

export const testPlatformLength = (platform, message, maxLength, tokens = []) =>
    (isPlatformWeighted(platform) ? testXWeightedLength : testEditorLength)(message, maxLength, tokens);

export const BASE_MESSAGE_LIMIT = 1500;
const MAX_DISPLAYED_MESSAGE_LIMIT = 5000;
export const BASE_MEDIA_LIMIT = 10;

const getGoverningPlatform = (platforms = []) =>
    platforms.reduce((best, p) => {
        const limit = PLATFORM_REQUIREMENTS[p]?.maxMessageLength;
        if (typeof limit !== 'number') return best;
        return limit > (PLATFORM_REQUIREMENTS[best]?.maxMessageLength ?? -Infinity) ? p : best;
    }, null);

export const getBaseMessageLimit = (platforms = []) => {
    const governing = getGoverningPlatform(platforms);
    return governing == null ? BASE_MESSAGE_LIMIT : PLATFORM_REQUIREMENTS[governing].maxMessageLength;
};

export const getDisplayedMessageLimit = limit => (limit <= MAX_DISPLAYED_MESSAGE_LIMIT ? limit : undefined);

export const getBaseMessageLengthCounter = (platforms = []) =>
    getPlatformLengthCounter(getGoverningPlatform(platforms));

export const testBaseMessageLength = (platforms, message, tokens = []) =>
    testPlatformLength(getGoverningPlatform(platforms), message, getBaseMessageLimit(platforms), tokens);

export const checkMediaValidityForPlatform = (platform, items = []) => {
    const videosCount = items.filter(m => m.type === MEDIA_TYPE.VIDEO).length;
    const imagesCount = items.filter(m => m.type === MEDIA_TYPE.IMAGE).length;
    if (isStoryPlatform(platform) && items.length > 1) {
        return { isValid: false, text: 'stories_allow_one_media' };
    }
    if (platform === POST_CHANNEL.GOOGLE && videosCount > 0) {
        return { isValid: false, text: 'videos_not_supported_by_google' };
    }
    // GooglePublisher publishes only the first image; flag >1 so extras aren't dropped silently.
    if (platform === POST_CHANNEL.GOOGLE && imagesCount > 1) {
        return { isValid: false, text: 'google_allow_one_image' };
    }
    if (platform === POST_CHANNEL.FACEBOOK && videosCount > 1) {
        return { isValid: false, text: 'facebook_allow_one_video' };
    }
    if (platform === POST_CHANNEL.FACEBOOK && imagesCount > 0 && videosCount > 0) {
        return { isValid: false, text: 'facebook_allow_one_type_of_media' };
    }
    if (platform === POST_CHANNEL.TIKTOK && videosCount > 1) {
        return { isValid: false, text: 'tiktok_allow_one_video' };
    }
    if (platform === POST_CHANNEL.TIKTOK && imagesCount > 0 && videosCount > 0) {
        return { isValid: false, text: 'tiktok_no_mixed_media' };
    }
    if (platform === POST_CHANNEL.TIKTOK && imagesCount > 35) {
        return { isValid: false, text: 'tiktok_max_photos_exceeded' };
    }
    if (platform === POST_CHANNEL.X && videosCount > 1) {
        return { isValid: false, text: 'x_allow_one_video' };
    }
    if (platform === POST_CHANNEL.X && imagesCount > 0 && videosCount > 0) {
        return { isValid: false, text: 'x_no_mixed_media' };
    }
    if (platform === POST_CHANNEL.X && imagesCount > 4) {
        return { isValid: false, text: 'x_max_photos_exceeded' };
    }
    if (platform === POST_CHANNEL.LINKEDIN && videosCount > 1) {
        return { isValid: false, text: 'linkedin_allow_one_video' };
    }
    if (platform === POST_CHANNEL.LINKEDIN && imagesCount > 0 && videosCount > 0) {
        return { isValid: false, text: 'linkedin_no_mixed_media' };
    }
    if (platform === POST_CHANNEL.LINKEDIN && imagesCount > 20) {
        return { isValid: false, text: 'linkedin_max_photos_exceeded' };
    }
    return { isValid: true, text: '' };
};

export const resolveOverrideMessage = (override, baseMessage) => {
    if (override?.message?.trim()) return override.message;
    return !override || isStoryPlatform(override.platform) ? baseMessage : '';
};

export const resolveOverrideMedia = (override, baseMedia) =>
    override && override.media != null ? override.media : baseMedia || [];

export const seedOverrideMessage = (platform, baseMessage) =>
    isStoryPlatform(platform) || !baseMessage?.trim() ? null : baseMessage;

export const seedOverrideMedia = (platform, baseMedia) =>
    isStoryPlatform(platform) && baseMedia?.length ? [baseMedia[0]] : null;

export const seedOverride = (platform, baseMessage, baseMedia) => ({
    platform,
    message: seedOverrideMessage(platform, baseMessage),
    media: seedOverrideMedia(platform, baseMedia),
});

const hasSameMedia = (a, b) => {
    if (a == null || b == null) return a == null && b == null;
    return a.length === b.length && a.every((item, i) => item.link === b[i].link);
};

export const isOverrideMessageMissing = override =>
    !!override && !isStoryPlatform(override.platform) && !override.message?.trim();

export const fillMissingOverrideMessages = (overrides, baseMessage) =>
    overrides.map(o =>
        isOverrideMessageMissing(o) ? { ...o, message: seedOverrideMessage(o.platform, baseMessage) } : o,
    );

export const canResetOverrideMessage = (override, baseMessage) => {
    const seed = seedOverrideMessage(override?.platform, baseMessage);
    return seed !== null && resolveOverrideMessage(override, baseMessage) !== seed;
};

export const canResetOverrideMedia = (override, baseMedia) =>
    !hasSameMedia(override.media, seedOverrideMedia(override.platform, baseMedia));

export const getPlatformIssues = (platform, message, media = [], tokens = []) => {
    const req = PLATFORM_REQUIREMENTS[platform];
    if (!req) return [];
    const issues = [];
    if (
        typeof req.maxMessageLength === 'number' &&
        !testPlatformLength(platform, message || '', req.maxMessageLength, tokens)
    ) {
        issues.push({ key: 'SocialPosts.messageTooLong', options: { count: req.maxMessageLength } });
    }
    const mediaValidity = checkMediaValidityForPlatform(platform, media);
    if (!mediaValidity.isValid) {
        issues.push({ key: `apiErrors.${mediaValidity.text}` });
    }
    if (req.requiresMedia && media.length === 0) {
        issues.push({ key: 'SocialPosts.mediaRequired' });
    }
    const { tooLarge, tooLong, tooSmall, tooWide, spec } = getVideoViolations(platform, media);
    if (tooLarge) {
        issues.push({ key: 'SocialPosts.videoTooLarge', options: { limit: formatVideoSize(spec.maxBytes) } });
    }
    if (tooLong) {
        issues.push({
            key: 'SocialPosts.videoTooLong',
            options: { limit: formatVideoDuration(spec.maxDurationSeconds) },
        });
    }
    if (tooSmall) {
        issues.push({ key: 'SocialPosts.videoTooSmall', options: { limit: formatVideoSize(spec.minBytes) } });
    }
    if (tooWide) {
        issues.push({
            key: 'SocialPosts.videoTooWide',
            options: { width: spec.maxWidth, height: spec.maxHeight },
        });
    }
    return issues;
};
